import { takeEvery, call, put, apply, select } from 'redux-saga/effects';
import rolesService from 'services/roles';
import staffInvitesService from 'services/staffInvites';
import organizationsService from 'services/organizations';
import { displaySuccessAlert } from 'services/alerts';
import { handleError } from 'services/alerts';
import {
    FETCH_ORGANIZATION_ROLES_REQUEST,
    FETCH_ORGANIZATION_ROLES_SUCCESS,
    FETCH_ORGANIZATION_ROLES_FAILURE,
    ASSIGN_ROLE_REQUEST,
    ASSIGN_ROLE_SUCCESS,
    ASSIGN_ROLE_FAILURE,
    REMOVE_ROLE_REQUEST,
    REMOVE_ROLE_SUCCESS,
    REMOVE_ROLE_FAILURE,
    INVITE_STAFF_REQUEST,
    INVITE_STAFF_SUCCESS,
    INVITE_STAFF_FAILURE,
    FETCH_PENDING_INVITES_REQUEST,
    FETCH_PENDING_INVITES_SUCCESS,
    FETCH_PENDING_INVITES_FAILURE,
    CANCEL_INVITE_REQUEST,
    CANCEL_INVITE_SUCCESS,
    CANCEL_INVITE_FAILURE,
    UPDATE_ORGANIZATION_NAME_REQUEST,
    UPDATE_ORGANIZATION_NAME_SUCCESS,
    UPDATE_ORGANIZATION_NAME_FAILURE,
    UPLOAD_STAFF_PHOTO_REQUEST,
    UPLOAD_STAFF_PHOTO_SUCCESS,
    UPLOAD_STAFF_PHOTO_FAILURE,
} from 'features/settings/staff/actions';

function* fetchOrganizationRoles(action) {
    try {
        const { organizationId } = action.payload;
        const response = yield call(rolesService.getOrganizationAccountRoles, organizationId);
        yield put({
            type: FETCH_ORGANIZATION_ROLES_SUCCESS,
            payload: { staffMembers: response },
        });
    } catch (error) {
        yield put({
            type: FETCH_ORGANIZATION_ROLES_FAILURE,
            payload: { error: error.message },
        });
    }
}

function* assignRole(action) {
    try {
        const { organizationId, accountId, role, title, bio, photo, name, last_name, instagram, tiktok, facebook, twitter } = action.payload;
        yield call(rolesService.assignOrganizationRole, organizationId, accountId, role, title, bio, photo, name, last_name, instagram, tiktok, facebook, twitter);
        yield put({
            type: ASSIGN_ROLE_SUCCESS,
            payload: { accountId, role, title, bio, photo, name, last_name, instagram, tiktok, facebook, twitter },
        });
    } catch (error) {
        // Extract error message from API response
        let errorMessage = 'An error occurred';
        if (error.json && error.json.message) {
            errorMessage = error.json.message;
        } else if (error.message) {
            errorMessage = error.message;
        }

        yield put({
            type: ASSIGN_ROLE_FAILURE,
            payload: { error: errorMessage },
        });
    }
}

function* removeRole(action) {
    try {
        const { organizationId, accountId } = action.payload;
        yield call(rolesService.removeOrganizationRole, organizationId, accountId);
        yield put({
            type: REMOVE_ROLE_SUCCESS,
            payload: { accountId },
        });
    } catch (error) {
        yield put({
            type: REMOVE_ROLE_FAILURE,
            payload: { error: error.message },
        });
    }
}

function* fetchPendingInvites(action) {
    try {
        const { organizationId } = action.payload;
        const response = yield call(staffInvitesService.getStaffInvites, organizationId);
        yield put({
            type: FETCH_PENDING_INVITES_SUCCESS,
            payload: { invites: response },
        });
    } catch (error) {
        yield put({
            type: FETCH_PENDING_INVITES_FAILURE,
            payload: { error: error.message },
        });
    }
}

function* inviteStaff(action) {
    try {
        const { organizationId, inviteData } = action.payload;
        const response = yield call(staffInvitesService.createStaffInvite, organizationId, inviteData);
        yield put({
            type: INVITE_STAFF_SUCCESS,
            payload: { invite: response },
        });
        
        // Show success notification
        yield call(displaySuccessAlert, `Staff invitation sent successfully to ${inviteData.email}!`);
        
        // Also refresh pending invites
        yield put({
            type: FETCH_PENDING_INVITES_REQUEST,
            payload: { organizationId },
        });
    } catch (error) {
        // Extract error message from API response
        let errorMessage = 'An error occurred';
        if (error.json && error.json.message) {
            errorMessage = error.json.message;
        } else if (error.message) {
            errorMessage = error.message;
        }
        
        yield put({
            type: INVITE_STAFF_FAILURE,
            payload: { error: errorMessage },
        });
    }
}

function* cancelInvite(action) {
    try {
        const { organizationId, inviteId } = action.payload;
        yield call(staffInvitesService.cancelStaffInvite, organizationId, inviteId);
        yield put({
            type: CANCEL_INVITE_SUCCESS,
            payload: { inviteId },
        });
    } catch (error) {
        yield put({
            type: CANCEL_INVITE_FAILURE,
            payload: { error: error.message },
        });
    }
}

function* updateOrganizationName(action) {
    try {
        const { organizationId, name } = action.payload;
        const response = yield call(organizationsService.patchOrganization, { id: organizationId, name });
        yield put({
            type: UPDATE_ORGANIZATION_NAME_SUCCESS,
            payload: { organization: response },
        });
        yield call(displaySuccessAlert, 'Organization name updated successfully');
    } catch (error) {
        yield put({
            type: UPDATE_ORGANIZATION_NAME_FAILURE,
            payload: { error: error.message },
        });
    }
}

function* uploadStaffPhoto(action) {
    try {
        const { organizationId, accountId, file } = action.payload;

        // Get presigned URL from server
        const payload = yield apply(rolesService, rolesService.uploadStaffPhoto, [organizationId, accountId]);

        // Upload to S3
        const response = yield fetch(payload.signed_url, {
            method: 'PUT',
            headers: {
                'Content-Type': 'image/png'
            },
            body: file,
        });

        if (!response.ok) {
            throw new Error(`S3 upload failed with status: ${response.status}`);
        }

        // Force refresh of image with cache-busting query param
        const photoUrl = `${payload.photo_url}?${new Date().getTime()}`;

        // Get current staff member data from Redux state to preserve existing fields
        const state = yield select();
        const staffMember = state.staff.staffMembers.find(member => member.account_id === accountId);

        if (!staffMember) {
            throw new Error('Staff member not found in Redux state');
        }

        // Persist the photo URL to the backend by updating the staff member
        yield call(rolesService.assignOrganizationRole,
            organizationId,
            accountId,
            staffMember.role,
            staffMember.title,
            staffMember.bio,
            photoUrl, // Updated photo URL
            staffMember.name,
            staffMember.last_name,
            staffMember.instagram,
            staffMember.tiktok,
            staffMember.facebook,
            staffMember.twitter
        );

        // Success - update with new photo URL
        yield put({
            type: UPLOAD_STAFF_PHOTO_SUCCESS,
            payload: { accountId, photo: photoUrl },
        });
    } catch (error) {
        yield handleError(error, 'Error: Unable to upload staff photo');
        yield put({
            type: UPLOAD_STAFF_PHOTO_FAILURE,
            payload: { error: error.message },
        });
    }
}

export default function* staffSaga() {
    yield takeEvery(FETCH_ORGANIZATION_ROLES_REQUEST, fetchOrganizationRoles);
    yield takeEvery(ASSIGN_ROLE_REQUEST, assignRole);
    yield takeEvery(REMOVE_ROLE_REQUEST, removeRole);
    yield takeEvery(INVITE_STAFF_REQUEST, inviteStaff);
    yield takeEvery(FETCH_PENDING_INVITES_REQUEST, fetchPendingInvites);
    yield takeEvery(CANCEL_INVITE_REQUEST, cancelInvite);
    yield takeEvery(UPDATE_ORGANIZATION_NAME_REQUEST, updateOrganizationName);
    yield takeEvery(UPLOAD_STAFF_PHOTO_REQUEST, uploadStaffPhoto);
}